import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import { useThemeColors } from '@/styles';
import { typography, radius } from '@/styles/design-tokens';
import type {
  WeekData,
  PriorityCount,
  StatusCount,
  ReflectionCount,
} from '@/types/learning-progress.types';
import { BoardTaskReflection } from '@/types/enums/BoardTaskReflection';

function aggregateByName<T extends { name: string; value: number }>(
  entries: T[]
): T[] {
  const byName = new Map<string, T>();
  for (const entry of entries) {
    const existing = byName.get(entry.name);
    if (existing) {
      existing.value += entry.value;
    } else {
      byName.set(entry.name, { ...entry });
    }
  }
  return Array.from(byName.values());
}

function hasAnyActivity(
  weeks: WeekData[],
  tasksByPriority: PriorityCount[],
  tasksByStatus: StatusCount[],
  tasksByReflection: ReflectionCount[]
): boolean {
  return (
    weeks.some(
      w =>
        w.tasks_created > 0 ||
        w.tasks_completed > 0 ||
        w.interactions_made > 0 ||
        w.pomodoros_estimated > 0 ||
        w.pomodoros_actual > 0
    ) ||
    tasksByPriority.length > 0 ||
    tasksByStatus.length > 0 ||
    tasksByReflection.length > 0
  );
}

const REFLECTION_LABELS: Record<string, string> = {
  [BoardTaskReflection.UNDERSTOOD]: 'Understood',
  [BoardTaskReflection.STILL_THINKING]: 'Still Thinking',
  [BoardTaskReflection.NEED_HELP]: 'Need Help',
};

function PomodoroSummaryStats({
  weeks,
  colors,
}: {
  weeks: WeekData[];
  colors: ReturnType<typeof useThemeColors>;
}) {
  const totalEstimated = weeks.reduce((s, w) => s + w.pomodoros_estimated, 0);
  const totalActual = weeks.reduce((s, w) => s + w.pomodoros_actual, 0);

  // How close was actual to estimated (100% = perfect, lower = further off)
  const avgAccuracy =
    totalEstimated > 0
      ? Math.max(
          0,
          Math.round(
            (1 - Math.abs(totalActual - totalEstimated) / totalEstimated) * 100
          )
        )
      : null;

  const stats = [
    { label: 'Estimated', value: totalEstimated, color: colors.purple },
    { label: 'Actual', value: totalActual, color: colors.success },
    {
      label: 'Avg Accuracy',
      value: avgAccuracy !== null ? `${avgAccuracy}%` : '—',
      color:
        avgAccuracy === null
          ? colors.text.tertiary
          : avgAccuracy >= 90
            ? colors.success
            : avgAccuracy >= 70
              ? colors.primary
              : colors.coral,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <h3
        className="text-sm font-medium"
        style={{ color: colors.text.secondary }}
      >
        Pomodoros
      </h3>
      <div className="flex gap-4">
        {stats.map(s => (
          <div
            key={s.label}
            className="flex-1 rounded-xl p-4 flex flex-col gap-1"
            style={{ backgroundColor: colors.offWhite }}
          >
            <span
              className="text-xs font-medium"
              style={{ color: colors.text.secondary }}
            >
              {s.label}
            </span>
            <span className="text-2xl font-semibold" style={{ color: s.color }}>
              {s.value}
            </span>
            <span className="text-xs" style={{ color: colors.text.tertiary }}>
              {weeks.length}w total
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface LearningProgressChartsProps {
  weeks: WeekData[];
  tasksByPriority: PriorityCount[];
  tasksByStatus: StatusCount[];
  tasksByReflection: ReflectionCount[];
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const colors = useThemeColors();
  return (
    <div
      className="rounded-xl shadow-soft p-5"
      style={{ backgroundColor: colors.offWhite }}
    >
      <h3
        className="text-sm font-medium mb-4"
        style={{ color: colors.text.secondary }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function formatWeekLabel(week: WeekData): string {
  const date = new Date(week.week_start);
  return `W${week.week_number} (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
}

export function LearningProgressCharts({
  weeks,
  tasksByPriority,
  tasksByStatus,
  tasksByReflection,
}: LearningProgressChartsProps) {
  const colors = useThemeColors();

  if (
    weeks.length === 0 ||
    !hasAnyActivity(weeks, tasksByPriority, tasksByStatus, tasksByReflection)
  ) {
    return (
      <div
        className="rounded-xl shadow-soft p-8 flex flex-col items-center justify-center text-center"
        style={{ backgroundColor: colors.offWhite, minHeight: 280 }}
      >
        <svg
          className="w-12 h-12 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{ color: colors.text.tertiary }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 13h2v8H3zM9 9h2v12H9zM15 5h2v16h-2zM21 1h2v20h-2z"
          />
        </svg>
        <h3
          className="text-base font-medium mb-1"
          style={{ color: colors.text.primary }}
        >
          No activity yet
        </h3>
        <p className="text-sm" style={{ color: colors.text.secondary }}>
          Start working on cards, complete tasks, or add comments to see your
          velocity stats here.
        </p>
        <p className="text-xs mt-2" style={{ color: colors.text.tertiary }}>
          Only cards with a start date are included in velocity stats.
        </p>
      </div>
    );
  }

  const weeklyData = weeks.map(w => ({
    name: formatWeekLabel(w),
    tasks_created: w.tasks_created,
    tasks_completed: w.tasks_completed,
    interactions_made: w.interactions_made,
    pomodoros_estimated: w.pomodoros_estimated,
    pomodoros_actual: w.pomodoros_actual,
    accuracy:
      w.pomodoros_estimated > 0
        ? Math.round((w.pomodoros_actual / w.pomodoros_estimated) * 100)
        : null,
  }));

  const chartColors = [
    colors.primary,
    colors.success,
    colors.blue,
    colors.purple,
    colors.teal,
    colors.coral,
    colors.rust,
    colors.forest,
  ];

  const tooltipStyle = {
    backgroundColor: colors.background,
    border: `1px solid ${colors.border.default}`,
    borderRadius: radius.md,
    color: colors.text.primary,
  };

  const priorityData = aggregateByName(
    tasksByPriority.map(p => ({ name: p.priority, value: p.count }))
  );

  const statusData = aggregateByName(
    tasksByStatus.map(s => ({ name: s.status_name, value: s.count }))
  );

  const reflectionData = aggregateByName(
    tasksByReflection.map(r => ({
      name: REFLECTION_LABELS[r.reflection] ?? r.reflection,
      value: r.count,
      reflection: r.reflection,
    }))
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Pomodoro summary stats */}
      <PomodoroSummaryStats weeks={weeks} colors={colors} />

      {/* Main activity charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Cards Started">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.border.light}
              />
              <XAxis
                dataKey="name"
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
              />
              <YAxis
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
                allowDecimals={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="tasks_created"
                fill={colors.primary}
                radius={[4, 4, 0, 0]}
                name="Cards Started"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cards Completed">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.border.light}
              />
              <XAxis
                dataKey="name"
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
              />
              <YAxis
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
                allowDecimals={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="tasks_completed"
                fill={colors.success}
                radius={[4, 4, 0, 0]}
                name="Cards Completed"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Comments Made">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.border.light}
              />
              <XAxis
                dataKey="name"
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
              />
              <YAxis
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
                allowDecimals={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="interactions_made"
                fill={colors.blue}
                radius={[4, 4, 0, 0]}
                name="Comments"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pomodoros: Estimated vs Actual">
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={weeklyData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.border.light}
              />
              <XAxis
                dataKey="name"
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
              />
              <YAxis
                yAxisId="count"
                tick={{
                  fill: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="pct"
                orientation="right"
                tickFormatter={v => `${v}%`}
                domain={[0, 'auto']}
                tick={{
                  fill: colors.text.tertiary,
                  fontSize: typography.sizes.xs,
                }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value, name) => {
                  if (name === 'accuracy') return [`${value}%`, 'Accuracy'];
                  return [
                    value,
                    name === 'pomodoros_estimated' ? 'Estimated' : 'Actual',
                  ];
                }}
              />
              <Legend
                wrapperStyle={{
                  color: colors.text.secondary,
                  fontSize: typography.sizes.xs,
                }}
                formatter={name => {
                  if (name === 'pomodoros_estimated') return 'Estimated';
                  if (name === 'pomodoros_actual') return 'Actual';
                  return 'Accuracy %';
                }}
              />
              <Bar
                yAxisId="count"
                dataKey="pomodoros_estimated"
                fill={colors.purple}
                radius={[4, 4, 0, 0]}
                name="pomodoros_estimated"
                barSize={16}
              />
              <Bar
                yAxisId="count"
                dataKey="pomodoros_actual"
                fill={colors.success}
                radius={[4, 4, 0, 0]}
                name="pomodoros_actual"
                barSize={16}
              />
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="accuracy"
                stroke={colors.coral}
                strokeWidth={2}
                dot={{ fill: colors.coral, r: 3 }}
                connectNulls={false}
                name="accuracy"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Priority, status & reflection distribution */}
      {(priorityData.length > 0 ||
        statusData.length > 0 ||
        reflectionData.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {priorityData.length > 0 && (
            <ChartCard title={`Cards by Priority (${weeks.length} Weeks)`}>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {priorityData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={chartColors[idx % chartColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    wrapperStyle={{
                      color: colors.text.secondary,
                      fontSize: typography.sizes.xs,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {statusData.length > 0 && (
            <ChartCard title={`Cards by Status (${weeks.length} Weeks)`}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 30, right: 30, bottom: 10, left: 30 }}>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="52%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={chartColors[idx % chartColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    wrapperStyle={{
                      color: colors.text.secondary,
                      fontSize: typography.sizes.xs,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {reflectionData.length > 0 && (
            <ChartCard title={`Cards by Reflection (${weeks.length} Weeks)`}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 30, right: 30, bottom: 10, left: 30 }}>
                  <Pie
                    data={reflectionData}
                    cx="50%"
                    cy="52%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {reflectionData.map((entry, idx) => {
                      const reflectionColor =
                        entry.reflection === BoardTaskReflection.UNDERSTOOD
                          ? colors.reflection.understood.text
                          : entry.reflection ===
                              BoardTaskReflection.STILL_THINKING
                            ? colors.reflection.stillThinking.text
                            : entry.reflection === BoardTaskReflection.NEED_HELP
                              ? colors.reflection.needHelp.text
                              : chartColors[idx % chartColors.length];
                      return <Cell key={idx} fill={reflectionColor} />;
                    })}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    wrapperStyle={{
                      color: colors.text.secondary,
                      fontSize: typography.sizes.xs,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
}
